import { Box3, BufferGeometry, Group, Matrix4, Mesh, Object3D, Vector3 } from 'three'
import convexHull from '@editor/utils/convexHull'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

type AlignmentConfig =
  | {
      referencePointIFCCS?: number[] | null
      referencePointModelCS?: number[] | null
      zRotationDegrees?: number | null
    }
  | null
  | undefined

/** Recompute a single object's `matrixWorld` from its local matrix and its parent's world matrix. */
function updateObjectWorldMatrix(object: Object3D): void {
  if (object.matrixAutoUpdate) object.updateMatrix()
  if (object.parent) object.matrixWorld.multiplyMatrices(object.parent.matrixWorld, object.matrix)
  else object.matrixWorld.copy(object.matrix)
}

/**
 * Iteratively refresh world matrices for `root` and its ancestors. Mirrors the approach in the
 * camera-origin util: recursive Three.js traversal helpers can overflow the call stack on very
 * large / deeply-nested GLB hierarchies.
 */
function updateWorldMatrices(root: Object3D): void {
  const ancestors: Object3D[] = []
  let ancestor = root.parent
  while (ancestor) {
    ancestors.push(ancestor)
    ancestor = ancestor.parent
  }
  for (let i = ancestors.length - 1; i >= 0; i--) {
    updateObjectWorldMatrix(ancestors[i])
  }

  const stack: Object3D[] = [root]
  while (stack.length > 0) {
    const object = stack.pop()!
    updateObjectWorldMatrix(object)
    for (let i = object.children.length - 1; i >= 0; i--) stack.push(object.children[i])
  }
}

/**
 * Matrix mapping a raw GLB node into aligned model space — replicating what the GLB viewer renders:
 * an X +90° rotation (GLTF Y-up → scene Z-up) followed by the alignment transform
 * `world = T(PModelRef) · Rz · T(-PIFCRef)`. Without an active alignment only the Z-up rotation is
 * applied (the model isn't aligned to the structural model yet).
 */
export function getAlignmentMatrix(config: AlignmentConfig, applyAlignment: boolean): Matrix4 {
  const zUp = new Matrix4().makeRotationX(Math.PI / 2)
  if (!applyAlignment || !config) return zUp

  const [ix, iy, iz] = config.referencePointIFCCS ?? [0, 0, 0]
  const [mx, my, mz] = config.referencePointModelCS ?? [0, 0, 0]
  const rz = new Matrix4().makeRotationZ(((config.zRotationDegrees ?? 0) * Math.PI) / 180)
  const t1 = new Matrix4().makeTranslation(-ix, -iy, -iz)
  const t2 = new Matrix4().makeTranslation(mx, my, mz)

  // world = t2 · Rz · t1 · zUp (matches the group chain in GLTFModelViewer)
  return new Matrix4().multiply(t2).multiply(rz).multiply(t1).multiply(zUp)
}

/**
 * Walk up from `object` to the closest ancestor whose name is one of the element guids (GLB nodes
 * are named by ifc guid; meshes hang below the element node).
 */
function findOwnerGuid(object: Object3D, guids: Set<string>): string | null {
  let owner: Object3D | null = object
  while (owner && !guids.has(owner.name)) owner = owner.parent
  return owner ? owner.name : null
}

/**
 * Aligned world-space `Box3` per element guid, built from the GLB source geometries (the batched
 * render mesh keeps the per-element node geometries, which are named by ifc guid). Each geometry
 * box is transformed by `alignmentMatrix · nodeMatrixWorld` into aligned model space.
 */
export function getGuidToAlignedBox(
  gltfModel: Group,
  guids: Set<string>,
  alignmentMatrix: Matrix4,
): Record<string, Box3> {
  updateWorldMatrices(gltfModel)

  const out: Record<string, Box3> = {}
  const elementMatrix = new Matrix4()
  const stack: Object3D[] = [gltfModel]

  while (stack.length > 0) {
    const object = stack.pop()!

    if (object instanceof Mesh && object.geometry) {
      const ownerGuid = findOwnerGuid(object, guids)
      if (ownerGuid) {
        if (!object.geometry.boundingBox) object.geometry.computeBoundingBox()
        if (object.geometry.boundingBox) {
          elementMatrix.multiplyMatrices(alignmentMatrix, object.matrixWorld)
          const box = object.geometry.boundingBox.clone().applyMatrix4(elementMatrix)
          const existing = out[ownerGuid]
          if (existing) existing.union(box)
          else out[ownerGuid] = box
        }
      }
    }

    for (let i = object.children.length - 1; i >= 0; i--) stack.push(object.children[i])
  }

  return out
}

export interface GlbElementGeometry {
  /** Aligned world-space bounding box of the element. */
  box: Box3
  /** Convex hull (CCW) of the element's mesh vertices projected onto the XY plane. */
  footprint: ImmutableVector3[]
  /**
   * Area-weighted mean face tilt from the horizontal, in degrees: ~0 for a flat plate (slab),
   * ~90 for an element dominated by vertical faces (wall), in between for pitched roofs.
   */
  dominantTiltDegrees: number
}

/** Accumulators collected per element guid during the mesh traversal. */
interface GeometryAccumulator {
  box: Box3
  points: ImmutableVector3[]
  faceArea: number
  horizontalFaceArea: number
}

function accumulateMeshGeometry(
  geometry: BufferGeometry,
  elementMatrix: Matrix4,
  acc: GeometryAccumulator,
): void {
  const position = geometry.getAttribute('position')
  if (!position) return

  const vertex = new Vector3()
  const vertices: Vector3[] = []
  for (let i = 0; i < position.count; i++) {
    vertex.fromBufferAttribute(position, i).applyMatrix4(elementMatrix)
    vertices.push(vertex.clone())
    acc.box.expandByPoint(vertex)
    acc.points.push(new ImmutableVector3(vertex.x, vertex.y, 0))
  }

  // Face tilt: iterate triangles (indexed or not); the cross-product length is 2 · triangle area
  // and its |z| component is 2 · area · |cos(tilt)|, so the sums area-weight the mean tilt.
  const index = geometry.getIndex()
  const triangleCount = index ? index.count / 3 : position.count / 3
  const ab = new Vector3()
  const ac = new Vector3()
  const normal = new Vector3()
  for (let t = 0; t < triangleCount; t++) {
    const ia = index ? index.getX(t * 3) : t * 3
    const ib = index ? index.getX(t * 3 + 1) : t * 3 + 1
    const ic = index ? index.getX(t * 3 + 2) : t * 3 + 2
    const a = vertices[ia]
    const b = vertices[ib]
    const c = vertices[ic]
    if (!a || !b || !c) continue
    ab.subVectors(b, a)
    ac.subVectors(c, a)
    normal.crossVectors(ab, ac)
    acc.faceArea += normal.length()
    acc.horizontalFaceArea += Math.abs(normal.z)
  }
}

/**
 * Aligned-space geometry summary (bounding box, 2D footprint hull, dominant face tilt) per
 * element guid, extracted from the GLB source geometries like `getGuidToAlignedBox` but from the
 * actual vertices, so footprints of rotated elements stay tight instead of axis-aligned.
 *
 * `guidToOwner` maps GLB node names (ifc guids) to the element guid the geometry belongs to. This
 * lets aggregated elements (e.g. multi-layer walls whose meshes hang under their
 * IfcBuildingElementPart children) collect the geometry of all their parts: map each child guid to
 * the parent element guid. Plain elements map to themselves.
 */
export function getGuidToElementGeometry(
  gltfModel: Group,
  guidToOwner: Record<string, string>,
  alignmentMatrix: Matrix4,
): Record<string, GlbElementGeometry> {
  updateWorldMatrices(gltfModel)

  const accumulators: Record<string, GeometryAccumulator> = {}
  const elementMatrix = new Matrix4()
  const stack: Object3D[] = [gltfModel]

  while (stack.length > 0) {
    const object = stack.pop()!

    if (object instanceof Mesh && object.geometry) {
      let node: Object3D | null = object
      while (node && !(node.name in guidToOwner)) node = node.parent
      const ownerGuid = node ? guidToOwner[node.name] : null
      if (ownerGuid) {
        const acc = (accumulators[ownerGuid] ??= {
          box: new Box3(),
          points: [],
          faceArea: 0,
          horizontalFaceArea: 0,
        })
        elementMatrix.multiplyMatrices(alignmentMatrix, object.matrixWorld)
        accumulateMeshGeometry(object.geometry, elementMatrix, acc)
      }
    }

    for (let i = object.children.length - 1; i >= 0; i--) stack.push(object.children[i])
  }

  const out: Record<string, GlbElementGeometry> = {}
  for (const [guid, acc] of Object.entries(accumulators)) {
    if (acc.points.length === 0 || acc.faceArea <= 0) continue
    const meanCosTilt = Math.min(1, acc.horizontalFaceArea / acc.faceArea)
    out[guid] = {
      box: acc.box,
      footprint: convexHull(acc.points),
      dominantTiltDegrees: (Math.acos(meanCosTilt) * 180) / Math.PI,
    }
  }
  return out
}
